// Sugalarm — kiosk-local voice agent client (cycle 100).
//
// These calls hit http://127.0.0.1:8765 directly, NOT the Fly API origin
// via apiClient. The agent runs on the Pi alongside Chromium and owns the
// Porcupine wake-word loop. Modeled on web/src/api/wifi.ts — same direct
// fetch + AbortController timeout + ApiResult shape.
//
// The SSE wake stream (`/voice/events`) is opened by the hook via
// EventSource, NOT through this client.
//
// Voice is KIOSK-ONLY. Web/mobile must make zero calls here — the hook
// (useVoiceAgent) gates on variant === "kiosk" + a /health probe +
// voice_supported before any of these run.

import type { ApiError, ApiErrorKind, ApiResult } from "../lib/types";

const AGENT_BASE = "http://127.0.0.1:8765";
const TIMEOUT_MS = 1500;

export interface VoiceHealth {
  ok: boolean;
  // The /health endpoint carries more fields (control / backend /
  // wifi_supported …); we only read what voice cares about.
  voice_supported: boolean;
}

export interface WakeOption {
  key: string;
  label: string;
}

export interface WakeOptions {
  options: WakeOption[];
  active: string | null;
}

export interface WakeSetResult {
  active: string | null;
}

export interface WakePausedResult {
  paused: boolean;
}

// The platform catalog of shipped wake words, with humanized labels. Used
// by Settings → CGM & devices so an owner can assign a wake word to ANY
// kiosk row from their phone without that Pi being reachable. When the
// local kiosk's agent IS reachable, the Settings UI prefers the agent's
// actual `/voice/wake/options` (the `.ppn` files truly present on that
// Pi). Extend this list as new wake words are trained + shipped.
export const WAKE_WORD_CATALOG: WakeOption[] = [
  { key: "hey-sugar", label: "Hey Sugar" },
  { key: "hey-buzz", label: "Hey Buzz" },
];

/** `"hey-buzz"` → `"Hey Buzz"` — for a server-stored key that isn't in
 *  the catalog (so the dropdown still renders it readably). */
export function humanizeWakeKey(key: string): string {
  const pretty = key
    .split("-")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
  return pretty || key;
}

function asError(kind: ApiErrorKind, status: number, message: string): ApiError {
  return { kind, status, message };
}

async function call<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs: number = TIMEOUT_MS,
): Promise<ApiResult<T>> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${AGENT_BASE}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers ?? {}),
      },
    });
    if (!res.ok) {
      const kind: ApiErrorKind =
        res.status === 422
          ? "validation"
          : res.status === 404
            ? "not_found"
            : res.status >= 500
              ? "server"
              : "unknown";
      return {
        ok: false,
        error: asError(kind, res.status, `agent returned ${res.status}`),
      };
    }
    const data = (await res.json()) as T;
    return { ok: true, data };
  } catch {
    return {
      ok: false,
      error: asError("network", 0, "kiosk agent unreachable"),
    };
  } finally {
    window.clearTimeout(t);
  }
}

export function getHealth(): Promise<ApiResult<VoiceHealth>> {
  return call<VoiceHealth>("/health");
}

export function getWakeOptions(): Promise<ApiResult<WakeOptions>> {
  return call<WakeOptions>("/voice/wake/options");
}

export function setWakeWord(key: string): Promise<ApiResult<WakeSetResult>> {
  return call<WakeSetResult>("/voice/wake", {
    method: "PUT",
    body: JSON.stringify({ key }),
  });
}

export function setWakePaused(
  paused: boolean,
  ttlMs?: number,
): Promise<ApiResult<WakePausedResult>> {
  return call<WakePausedResult>("/voice/wake_paused", {
    method: "PUT",
    body: JSON.stringify({ paused, ttl_ms: ttlMs ?? null }),
  });
}

export const EVENTS_URL = `${AGENT_BASE}/voice/events`;
